import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Code2, Globe, Package } from "lucide-react";

import { CONNECTORS } from "@/lib/connectorCatalog";
import { ConnectorCatalog } from "@/components/ConnectorCatalog";
import { Button } from "@/components/ui/button";
import { CreatePage, Section, useStepper, type WizardStep } from "@/components/wizard";
import { Spinner } from "@/components/Page";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { useConnectForm, type ConnectKind } from "@/components/connect/useConnectForm";
import {
  AuthPicker,
  ConnectorBanner,
  HttpFields,
  NameField,
  OpenAPIFields,
  StdioArgs,
  StdioEnvTable,
  StdioFields,
} from "@/components/connect/sections";

const STEPS: WizardStep[] = [
  { id: "choose", title: "Choose", description: "Pick a service or connect your own" },
  { id: "configure", title: "Configure", description: "Set it up and connect" },
];

// The three "custom" connect paths offered alongside the catalog.
const CUSTOM = [
  { kind: "http" as ConnectKind, icon: Globe, title: "MCP server", blurb: "Connect any remote MCP server by its URL." },
  {
    kind: "openapi" as ConnectKind,
    icon: Code2,
    title: "API / REST",
    blurb: "Turn an OpenAPI spec or a single request into tools.",
  },
  {
    kind: "stdio" as ConnectKind,
    icon: Package,
    title: "npm / PyPI package",
    blurb: "Run a published MCP package in an isolated sandbox.",
  },
];

// ConnectServer is the dedicated, spacious page for connecting a NEW integration.
// Step 1 picks what to connect (catalog or a custom path); step 2 configures and
// connects it. Reconnect/edit of an EXISTING integration stay as dialogs on the
// Servers list. A ?connect=<id> deep-link jumps straight to step 2, prefilled.
export function ConnectServer() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const stepper = useStepper(STEPS.length);

  const form = useConnectForm({
    onConnected: (_name, building) => {
      toast.success(building ? "Installing… building the package" : "Integration connected");
      navigate("/servers");
    },
    onError: (m) => toast.error(m),
  });

  // Deep-link from a catalog tile (?connect=<id>): apply the connector and skip
  // straight to step 2.
  useEffect(() => {
    const id = params.get("connect");
    if (!id) return;
    const c = CONNECTORS.find((x) => x.id === id);
    if (c) {
      form.applyConnector(c);
      stepper.next();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancel = () => navigate("/servers");

  const footer =
    stepper.current === 0 ? (
      <Button type="button" variant="ghost" className="mr-auto" onClick={onCancel}>
        Cancel
      </Button>
    ) : (
      <>
        <Button type="button" variant="ghost" className="mr-auto" onClick={onCancel} disabled={form.busy}>
          Cancel
        </Button>
        <Button type="button" variant="outline" onClick={stepper.back} disabled={form.busy}>
          Back
        </Button>
        <Button type="button" onClick={() => void form.submit()} disabled={form.busy || !form.submittable}>
          {form.busy && <Spinner />} {form.submitLabel}
        </Button>
      </>
    );

  return (
    <CreatePage
      title="Connect an integration"
      description="Give your agents a new tool — an app, an API, or a published package. Credentials are stored encrypted and never reach the sandbox."
      backTo="/servers"
      steps={STEPS}
      current={stepper.current}
      furthest={stepper.furthest}
      onJump={stepper.goTo}
      footer={footer}
      wide={stepper.current === 0}
    >
      {stepper.current === 0 ? (
        <>
          <Section title="Browse the catalog" description="Popular services, ready to connect.">
            <ConnectorCatalog
              onPick={(c) => {
                form.applyConnector(c);
                stepper.next();
              }}
            />
          </Section>
          <Section title="Connect something custom" description="Not in the catalog? Connect it directly.">
            <div className="grid gap-3 sm:grid-cols-3">
              {CUSTOM.map(({ kind, icon: Icon, title, blurb }) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => {
                    form.chooseManual(kind);
                    stepper.next();
                  }}
                  className={cn(
                    "group flex flex-col items-start gap-2 rounded-xl border bg-card p-4 text-left transition-colors",
                    "hover:border-primary/40 hover:shadow-sm",
                  )}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:text-foreground">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium">{title}</span>
                  <span className="text-xs text-muted-foreground">{blurb}</span>
                </button>
              ))}
            </div>
          </Section>
        </>
      ) : (
        <>
          <ConnectorBanner form={form} />
          {form.isStdio ? (
            <>
              <Section title="Package" description="The published MCP package to run.">
                <NameField form={form} />
                <StdioFields form={form} />
              </Section>
              <Section
                title="Environment variables"
                description="Configuration the package needs (API keys, endpoints). Use a plain value or attach a saved secret. Stored encrypted."
              >
                <StdioEnvTable form={form} />
              </Section>
              <Section title="Arguments" description="Extra command-line flags for the package.">
                <StdioArgs form={form} />
              </Section>
            </>
          ) : form.isOpenAPI ? (
            <>
              <Section title="Details">
                <NameField form={form} />
              </Section>
              <Section title="Request" description="Describe the API; CodeBridge turns it into typed tools.">
                <OpenAPIFields form={form} />
              </Section>
              <Section title="Authentication" description="How CodeBridge authenticates to the API.">
                <AuthPicker form={form} />
              </Section>
            </>
          ) : (
            <>
              <Section title="Details">
                <NameField form={form} />
                <HttpFields form={form} />
              </Section>
              <Section title="Authentication" description="How CodeBridge authenticates to the server.">
                <AuthPicker form={form} />
              </Section>
            </>
          )}
          {form.err && <p className="text-sm font-medium text-destructive">{form.err}</p>}
        </>
      )}
    </CreatePage>
  );
}
